import React, { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MoreVertical } from "lucide-react";
import { ARROW_LEFT, GREEN_CLOSE_BTN } from "../../../assets";
import TableComponent from "../../../shared/components/table";
import { SortingState, createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "@mui/material";
import { Menu, Transition } from "@headlessui/react";
import ModalV2 from "../../../shared/components/modalV2";
import SuccessModalV2 from "../../../shared/components/successModal/sucessModalV2";
import RenameModal from "../admin/case-folder/modals/RenameModal";
import WarningPromptModal from "../admin/case-folder/modals/WarningPromptModal";

type TeamMember = {
  id: number;
  sn: boolean;
  name: string;
  email: string;
  role: string;
  details: string;
};

const columnHelper = createColumnHelper<TeamMember>();

const createColumns = (
  onChangeRole: (member: TeamMember) => void,
  onRemoveTeam: (member: TeamMember) => void,
) => [
  columnHelper.accessor("sn", {
    header: "SN",
    cell: ({ row }) => (
      <Checkbox
        color="success"
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        sx={{
          color: "#E0E0E0",
          "&.Mui-checked": {
            color: "#00B074",
          },
        }}
      />
    ),
  }),
  columnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <span className="text-[#101828] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("email", {
    header: "Email",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("role", {
    header: "Role",
    cell: (info) => (
      <span className="text-[#101828] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.accessor("details", {
    header: "Details",
    cell: (info) => (
      <span className="text-[#667085] text-sm">{info.getValue()}</span>
    ),
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    cell: (info) => (
      <ActionMenu
        member={info.row.original}
        onChangeRole={onChangeRole}
        onRemoveTeam={onRemoveTeam}
      />
    ),
  }),
];

const ActionMenu = ({
  member,
  onChangeRole,
  onRemoveTeam,
}: {
  member: TeamMember;
  onChangeRole: (member: TeamMember) => void;
  onRemoveTeam: (member: TeamMember) => void;
}) => {
  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button className="p-2 text-[#98A2B3] hover:text-[#475467] transition-colors focus:outline-none">
        <MoreVertical className="w-5 h-5" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items
          anchor="bottom end"
          className="absolute right-0 mt-2 w-44 origin-top-right rounded-xl bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50 py-2"
        >
          <div className="px-1 py-1 flex flex-col gap-y-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onChangeRole(member)}
                  className={`${
                    active ? "bg-[#D1FADF] text-[#027A48]" : "text-[#101828]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                >
                  Change role
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => onRemoveTeam(member)}
                  className={`${
                    active ? "bg-gray-50 text-[#B42318]" : "text-[#B42318]"
                  } group flex w-full items-center rounded-lg px-4 py-2.5 text-sm`}
                >
                  Remove team
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

type AddTeamMemberModalProps = {
  open: boolean;
  onClose: () => void;
  onAdd: (member: Omit<TeamMember, "id" | "sn">) => void;
};

const AddTeamMemberModal = ({
  open,
  onClose,
  onAdd,
}: AddTeamMemberModalProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Prosecutor (Counsel)");

  const handleSubmit = () => {
    if (!firstName || !lastName || !email || !role) return;
    onAdd({
      name: `${firstName} ${lastName}`,
      email,
      role,
      details: "May 3, 2024",
    });
    setFirstName("");
    setLastName("");
    setEmail("");
    setRole("Prosecutor (Counsel)");
    onClose();
  };

  return (
    <ModalV2
      isOpen={open}
      isClose={onClose}
      maxWidth="w-[450px]"
      edges="rounded-2xl"
    >
      <div className="px-8 py-8 flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 flex items-center justify-center">
            <h2 className="text-xl font-semibold text-[#101828]">
              Add a team member
            </h2>
          </div>
          <button onClick={onClose}>
            <img src={GREEN_CLOSE_BTN} className="w-[17px] h-[17px]" alt="" />
          </button>
        </div>

        <div className="flex flex-col gap-4 w-full">
          <div className="flex flex-col gap-1 items-start w-full">
            <label className="text-sm font-normal text-[#98A2B3]">
              First name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="First name"
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
            />
          </div>
          <div className="flex flex-col gap-1 items-start w-full">
            <label className="text-sm font-normal text-[#98A2B3]">
              Last name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Last name"
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
            />
          </div>
          <div className="flex flex-col gap-1 items-start w-full">
            <label className="text-sm font-normal text-[#98A2B3]">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg placeholder:text-[#98A2B3] text-customDark"
            />
          </div>
          <div className="flex flex-col gap-1 items-start w-full">
            <label className="text-sm font-normal text-[#98A2B3]">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full p-3 text-sm border border-[#CACACA] rounded-lg bg-white text-customDark"
            >
              <option value="Prosecutor (Counsel)">Prosecutor (Counsel)</option>
              <option value="Legal Officer (Counsel)">
                Legal Officer (Counsel)
              </option>
              <option value="Paralegal">Paralegal</option>
            </select>
          </div>
        </div>

        <button
          onClick={handleSubmit}
          className="mt-2 w-full h-11 rounded-lg bg-[#009444] text-white text-sm font-semibold"
        >
          Add
        </button>
      </div>
    </ModalV2>
  );
};

function Teams() {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([
    {
      id: 1,
      sn: false,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Prosecutor",
      details: "May 3, 2024",
    },
    {
      id: 2,
      sn: false,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Prosecutor",
      details: "May 3, 2024",
    },
    {
      id: 3,
      sn: false,
      name: "Esther Ochendo",
      email: "Esthero@gmail.com",
      role: "Legal Officer",
      details: "May 3, 2024",
    },
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showRoleChangedModal, setShowRoleChangedModal] = useState(false);
  const [showTeamRemovedModal, setShowTeamRemovedModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [roleName, setRoleName] = useState("");

  const columns = useMemo(
    () =>
      createColumns(
        (member) => {
          setSelectedMember(member);
          setRoleName(member.role);
          setShowRenameModal(true);
        },
        (member) => {
          setSelectedMember(member);
          setShowWarningModal(true);
        },
      ),
    [],
  );

  const handleConfirmChangeRole = () => {
    if (selectedMember) {
      setTeamMembers((prev) =>
        prev.map((item) =>
          item.id === selectedMember.id ? { ...item, role: roleName } : item,
        ),
      );
    }
    setShowRenameModal(false);
    setShowRoleChangedModal(true);
  };

  const handleConfirmRemoveTeam = () => {
    if (selectedMember) {
      setTeamMembers((prev) =>
        prev.filter((item) => item.id !== selectedMember.id),
      );
    }
    setShowWarningModal(false);
    setShowTeamRemovedModal(true);
  };

  const handleAddMember = (member: Omit<TeamMember, "id" | "sn">) => {
    setTeamMembers((prev) => [
      ...prev,
      { id: prev.length + 1, sn: false, ...member },
    ]);
  };

  return (
    <div className="flex flex-col h-full min-h-screen">
      <div className="bg-white">
        <div className="px-8 pt-6 pb-4 border-b flex items-center justify-between w-full border-[#E9EAEB]">
          <h1 className="text-[28px] font-bold text-[#1A1A1A]">Teams</h1>
          <button
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#D0D5DD] bg-white text-sm font-medium text-[#111827]"
            onClick={() => setShowAddModal(true)}
          >
            <Plus className="w-4 h-4" />
            <span>Add team member</span>
          </button>
        </div>
        <div className="px-8 py-3 border-b border-[#E9EAEB] flex items-center justify-between">
          <div className="flex items-center gap-x-2 text-sm">
            <Link
              to="/police/overview"
              className="text-[#98A2B3] hover:text-[#00B074]"
            >
              Home
            </Link>
            <img src={ARROW_LEFT} alt="" className="w-[14px] h-[14px]" />
            <span className="font-medium text-[#1A1A1A]">Team</span>
          </div>
        </div>
      </div>

      <div className="p-8 flex flex-col gap-y-6">
        <div className="bg-white overflow-hidden">
          <TableComponent
            DATA={teamMembers}
            // @ts-ignore
            COLUMNS={columns}
            sorting={sorting}
            setSorting={setSorting}
          />
        </div>
      </div>

      <AddTeamMemberModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddMember}
      />

      <RenameModal
        isOpen={showRenameModal}
        onClose={() => setShowRenameModal(false)}
        name={roleName}
        setName={setRoleName}
        onClick={handleConfirmChangeRole}
      />

      <WarningPromptModal
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        message="Are you sure you want to remove team member?"
        onClick={handleConfirmRemoveTeam}
      />

      <SuccessModalV2
        open={showRoleChangedModal}
        close={() => setShowRoleChangedModal(false)}
        message="Role changed successfully"
      />

      <SuccessModalV2
        open={showTeamRemovedModal}
        close={() => setShowTeamRemovedModal(false)}
        message="Team member removed successfully"
      />
    </div>
  );
}

export default Teams;

